import 'keen-slider/keen-slider.scss';

import { generateMarketPathname } from '@chewy/market-handler';
import { useKeenSlider } from 'keen-slider/react';
import Image from 'next/image';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { SEGMENT_TAGS } from '@/analytics/tag-definitions';
import { PetProfileModel } from '@/generated-client/pet-profile-a';
import { useFeatureValue } from '@/hooks/useFeatureValue';
import CarouselCat from '@/images/carousel-cat.svg';
import TestimonialCarouselDog from '@/images/testimonial-carousel-dog.svg';
import { useAnalyticsContext } from '@/providers/AnalyticsProvider';
import { useMarket } from '@/providers/MarketProvider';
import { FeatureConfigKeys } from '@/utils/athena-feature-config';
import { getPetType, PetType } from '@/utils/pet-profiles';

import { Arrows } from './Arrows/Arrows';
import styles from './CustomerQuotesCarousel.module.scss';
import { Dots } from './Dots/Dots';
import { QuoteSlide } from './QuoteSlide/QuoteSlide';

interface Props {
  petProfiles?: PetProfileModel[];
}

type Quote = {
  quote: string;
  author: string;
};

export type QuotesResponse = {
  quotes: Quote[];
};

export const CustomerQuotesCarousel = ({ petProfiles }: Props) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const { market } = useMarket();
  const [loaded, setLoaded] = useState(false);

  const { t } = useTranslation();

  const customerQuotes = useFeatureValue(FeatureConfigKeys.CUSTOMER_QUOTES, { quotes: [] });
  const { trackSegmentEvent } = useAnalyticsContext();

  const [sliderRef, sliderInstanceRef] = useKeenSlider<HTMLDivElement>({
    initial: 0,
    slideChanged(slider) {
      setCurrentSlide(slider.track.details.rel);
      trackSegmentEvent(
        SEGMENT_TAGS.ScrollsThroughQuotes.eventName,
        SEGMENT_TAGS.ScrollsThroughQuotes.data,
      );
    },
    created() {
      setLoaded(true);
      trackSegmentEvent(
        SEGMENT_TAGS.ViewsQuotesCarousel.eventName,
        SEGMENT_TAGS.ViewsQuotesCarousel.data,
      );
    },
  });

  const quotes = customerQuotes?.quotes;

  if (!quotes) return null;

  const hasJustCat = petProfiles && getPetType(petProfiles) === PetType.CAT;

  return (
    <div className={styles.customerQuotesContainer} data-testid="customer-quotes-carousel">
      <div className={styles.curvedContainer}>
        {hasJustCat ? (
          <Image
            alt="cat laying down"
            className={styles.carouselCat}
            height={240}
            priority
            src={generateMarketPathname(CarouselCat.src, market)}
            width={240}
          />
        ) : (
          <Image
            alt="dog laying down"
            className={styles.carouselDog}
            height={240}
            priority
            src={generateMarketPathname(TestimonialCarouselDog.src, market)}
            width={240}
          />
        )}
        <>
          <div className={styles.navigationWrapper}>
            <div>
              <h6 className={styles.cta}>{t('loyalty.customer-quotes.cta')}</h6>
              <div className={`keen-slider`} ref={sliderRef}>
                {quotes.map(({ quote, author }) => (
                  <QuoteSlide author={author} key={quote} market={market} quote={quote} />
                ))}
              </div>
            </div>
            <Arrows
              currentSlide={currentSlide}
              loaded={loaded}
              sliderInstanceRef={sliderInstanceRef}
            />
          </div>
          <Dots currentSlide={currentSlide} loaded={loaded} sliderInstanceRef={sliderInstanceRef} />
        </>
      </div>
    </div>
  );
};
